/**
 * Central Environment Configuration
 * ─────────────────────────────────────────────────────────────────────────────
 * Single source of truth for all environment variables.
 * Import `env` from this file instead of accessing `import.meta.env` directly.
 *
 * Usage:
 *   import { env } from '@/config/env';
 *   fetch(`${env.WORKER_URL}/api/...`);
 */

export type TenantResolutionMode = 'domain' | 'session';

interface EnvConfig {
  /** Backend worker base URL (no trailing slash) */
  WORKER_URL: string;

  /** Current environment mode */
  MODE: 'development' | 'staging' | 'production';

  /** Whether running in production */
  IS_PROD: boolean;

  /** Whether running in development */
  IS_DEV: boolean;

  /** App name */
  APP_NAME: string;

  /** App version (from package.json via Vite define) */
  APP_VERSION: string;

  /** Base domain for tenant resolution (e.g. 'fans.audio'). No hardcoded fallback. */
  BASE_DOMAIN: string;

  /** Tenant resolution mode: 'domain' or 'session' */
  TENANT_RESOLUTION: TenantResolutionMode;
}

/**
 * Derive the default base domain from WORKER_URL when BASE_DOMAIN is not set.
 * Provides portability: works in YouWare staging, testing, and production.
 */
function getDefaultDomain(workerUrl: string): string {
  try {
    const url = new URL(workerUrl);
    return normalizeBaseDomain(url.hostname);
  } catch {
    return 'localhost';
  }
}

function normalizeBaseDomain(hostname: string): string {
  const host = hostname.trim().toLowerCase();
  if (!host) return host;
  if (/^(localhost|127\.0\.0\.1)$/.test(host)) return host;

  const parts = host.split('.').filter(Boolean);
  if (parts.length < 3) {
    return host;
  }

  // Strip a leading subdomain (tenant/api/www/etc.) and keep the base domain.
  return parts.slice(1).join('.');
}

function getEnvConfig(): EnvConfig {
  const mode = (import.meta.env.MODE ?? 'development') as EnvConfig['MODE'];

  let workerUrl = (
    import.meta.env.VITE_WORKER_URL ||
    'https://staging--2ubyxk1ri9uztazfbi9h.youbase.cloud'
  ).replace(/\/$/, ''); // strip trailing slash

  const configuredBaseDomain = (import.meta.env.VITE_BASE_DOMAIN || '').trim();
  const workerLooksLocal = /(^|\/\/)(localhost|127\.0\.0\.1)(:\d+)?($|\/)/i.test(workerUrl);
  const browserHost = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const browserOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  const browserIsLocal = /^(localhost|127\.0\.0\.1)$/.test(browserHost);

  // Safety guard: production builds should never call localhost APIs from non-local browser hosts.
  // This handles accidental local VITE_WORKER_URL values baked into a deployed bundle.
  if (mode === 'production' && workerLooksLocal && browserOrigin && !browserIsLocal) {
    if (
      configuredBaseDomain &&
      (browserHost === configuredBaseDomain.toLowerCase() || browserHost === `www.${configuredBaseDomain.toLowerCase()}`)
    ) {
      workerUrl = `https://api.${configuredBaseDomain}`;
    } else {
      workerUrl = browserOrigin;
    }
    console.warn('[env] Localhost WORKER_URL detected on non-local production host; falling back to', workerUrl);
  }

  else if (mode === 'production' && workerLooksLocal && configuredBaseDomain) {
    workerUrl = `https://api.${configuredBaseDomain}`;
    console.warn('[env] VITE_WORKER_URL pointed at localhost in production build; falling back to', workerUrl);
  }

  // On wildcard tenant hosts served through Worker routing, prefer same-origin API calls
  // so auth/OTP does not depend on cross-origin CORS behavior.
  if (typeof window !== 'undefined' && configuredBaseDomain) {
    const host = window.location.hostname.toLowerCase();
    const base = configuredBaseDomain.toLowerCase();
    const isTenantSubdomain = host.endsWith(`.${base}`) && host !== base && !host.startsWith('api.');
    if (isTenantSubdomain) {
      workerUrl = window.location.origin;
    }
  }

  const baseDomain = configuredBaseDomain
    || ((browserHost && !browserIsLocal) ? normalizeBaseDomain(browserHost) : getDefaultDomain(workerUrl));

  const tenantResolution: TenantResolutionMode =
    import.meta.env.VITE_TENANT_RESOLUTION === 'domain' ? 'domain' : 'session';

  return {
    WORKER_URL: workerUrl,
    MODE: mode,
    IS_PROD: mode === 'production',
    IS_DEV: mode === 'development',
    APP_NAME: import.meta.env.VITE_APP_NAME || 'fans.audio',
    APP_VERSION: import.meta.env.VITE_APP_VERSION || '0.0.0',
    BASE_DOMAIN: baseDomain,
    TENANT_RESOLUTION: tenantResolution,
  };
}

/** Validated, typed environment configuration */
export const env = getEnvConfig();
